import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Trash2, Copy, ExternalLink, Loader2, Search,
  TriangleAlert, TrendingUp, LayoutTemplate, Mail, Upload, ClipboardList, FileSignature,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { EASE } from "@/components/Reveal";
import BusinessPlan from "@/components/BusinessPlan";
import { ProposalCode } from "@/components/ProposalCode";
import { AccessGate, getStoredCode, applyAccessCode } from "@/components/AccessGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["Ristorante / Pizzeria", "Hotel / Resort", "B&B / Domus", "Bottega / Negozio", "Altro"];
const STATUSES = ["da_contattare", "contattato", "in_trattativa", "cliente", "scartato"];
const STATUS_LABEL = {
  da_contattare: "Da contattare",
  contattato: "Contattato",
  in_trattativa: "In trattativa",
  cliente: "Cliente",
  scartato: "Scartato",
};

const inputCls =
  "w-full rounded-xl border border-line bg-panel px-4 py-3 text-sm text-bone placeholder:text-faded transition-colors duration-300 focus:border-terracotta focus:outline-none";

const selectCls = `${inputCls} appearance-none`;

export default function Prospects() {
  const [unlocked, setUnlocked] = useState(() => {
    const stored = getStoredCode();
    if (stored) applyAccessCode(stored);
    return Boolean(stored);
  });
  if (!unlocked) return <AccessGate onOk={() => setUnlocked(true)} />;
  return <ProspectsDashboard />;
}

function ProspectsDashboard() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("tutte");
  const [statusFilter, setStatusFilter] = useState("tutti");
  const [importText, setImportText] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [reports, setReports] = useState({});
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("prospect");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/prospects`);
      setProspects(data);
    } catch (e) {
      toast.error("Caricamento elenco non riuscito");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cities = useMemo(() => [...new Set(prospects.map((p) => p.city).filter(Boolean))], [prospects]);

  const filtered = prospects.filter(
    (p) =>
      (typeFilter === "tutte" || p.business_type === typeFilter) &&
      (statusFilter === "tutti" || p.status === statusFilter) &&
      (!query ||
        `${p.name} ${p.city} ${p.region || ""} ${p.website || ""}`.toLowerCase().includes(query.toLowerCase()))
  );

  const doImport = async () => {
    if (!importText.trim()) {
      toast.error("Incolla prima almeno una riga");
      return;
    }
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/prospects/import`, { text: importText });
      toast.success(`${data.imported} attività importate`);
      setImportText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Importazione non riuscita");
    } finally {
      setImporting(false);
    }
  };

  const analyze = async (p) => {
    setAnalyzingId(p.id);
    setOpenId(p.id);
    try {
      const { data } = await axios.post(`${API}/prospects/${p.id}/analyze`);
      setReports((r) => ({ ...r, [p.id]: data }));
      setProspects((ps) => ps.map((x) => (x.id === p.id ? { ...x, has_report: true } : x)));
      toast.success("Analisi pronta!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analisi non riuscita");
      setOpenId(null);
    } finally {
      setAnalyzingId(null);
    }
  };

  const openReport = async (p) => {
    if (reports[p.id]) {
      setOpenId(p.id);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/prospects/${p.id}/report`);
      setReports((r) => ({ ...r, [p.id]: data }));
      setOpenId(p.id);
    } catch (e) {
      toast.info("Nessun report: genera prima l'analisi IA");
    }
  };

  const setStatus = async (p, status) => {
    try {
      await axios.patch(`${API}/prospects/${p.id}`, { status });
      setProspects((ps) => ps.map((x) => (x.id === p.id ? { ...x, status } : x)));
    } catch (e) {
      toast.error("Aggiornamento stato non riuscito");
    }
  };

  const remove = async (p) => {
    try {
      await axios.delete(`${API}/prospects/${p.id}`);
      setProspects((ps) => ps.filter((x) => x.id !== p.id));
      toast.success("Attività eliminata");
    } catch (e) {
      toast.error("Eliminazione non riuscita");
    }
  };

  const copyEmail = async (rep) => {
    try {
      await navigator.clipboard.writeText(`Oggetto: ${rep.email_oggetto}\n\n${rep.email_corpo}`);
      toast.success("Email copiata negli appunti");
    } catch (e) {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <main className="min-h-screen bg-ink pb-24">
      <header className="glass sticky top-0 z-40 border-b border-line">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <span className="font-serif text-lg tracking-tight text-bone">
              Area di lavoro<span className="text-terracotta">.</span>
            </span>
          </div>
          <Link
            to="/"
            data-testid="prospect-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 font-accent text-[10px] uppercase tracking-[0.2em] text-stone transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
          >
            <ArrowLeft size={13} />
            Torna al sito
          </Link>
        </div>
      </header>

      <div className="border-b border-line">
        <div className="mx-auto flex max-w-7xl gap-1 px-5 pt-4 sm:px-8">
          {[
            { id: "prospect", label: "Ricerca clienti", icon: ClipboardList, testid: "tab-prospect" },
            { id: "piano", label: "Business plan", icon: TrendingUp, testid: "tab-business-plan" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-5 py-3 font-accent text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                tab === t.id
                  ? "border-terracotta text-bone"
                  : "border-transparent text-faded hover:text-stone"
              }`}
            >
              <t.icon size={13} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "piano" ? (
        <div className="mx-auto max-w-7xl px-5 pt-12 sm:px-8" data-testid="business-plan-panel">
          <p className="flex items-center gap-3 font-accent text-[11px] font-medium uppercase tracking-[0.32em] text-terracotta">
            <span className="inline-block h-px w-8 bg-terracotta" />
            Numeri del business
          </p>
          <h1 className="mt-4 font-serif text-3xl font-medium tracking-tight text-bone sm:text-4xl lg:text-5xl">
            Business plan <em className="italic text-terracotta">Nex Studio Design.</em>
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone sm:text-base">
            Costi e ricavi per ogni singolo sito realizzato e mantenuto: hosting, domini, canoni e
            scenari al crescere dei clienti.
          </p>
          <BusinessPlan />
        </div>
      ) : (
      <div className="mx-auto max-w-7xl px-5 pt-12 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="flex items-center gap-3 font-accent text-[11px] font-medium uppercase tracking-[0.32em] text-terracotta">
              <span className="inline-block h-px w-8 bg-terracotta" />
              Ricerca clienti
            </p>
            <h1 className="mt-4 font-serif text-3xl font-medium tracking-tight text-bone sm:text-4xl lg:text-5xl">
              Le attività da <em className="italic text-terracotta">conquistare.</em>
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone sm:text-base">
              Elenco per categoria e città, analisi IA del sito attuale, bozza del nuovo sito e
              proposta email pronta da inviare dalla tua casella.
            </p>
          </div>
          <button
            onClick={() => setShowImport(!showImport)}
            data-testid="prospect-import-toggle"
            className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3.5 font-accent text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
          >
            <Upload size={14} />
            {showImport ? "Chiudi importazione" : "Importa attività"}
          </button>
        </div>

        {showImport && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mt-8 rounded-3xl border border-line bg-panel p-6 sm:p-8"
          >
            <p className="font-accent text-[11px] uppercase tracking-[0.28em] text-terracotta">
              Importa elenco
            </p>
            <p className="mt-2 text-sm text-stone">
              Una attività per riga, campi separati da punto e virgola:
              <span className="text-bone"> Nome; Tipologia; Città; Sito web; Email</span>. Tipologia
              e sito sono opzionali. Da Google Maps cerca le attività della zona, poi incolla qui.
            </p>
            <textarea
              rows={6}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={"Trattoria dai Nostri; Ristorante / Pizzeria; Firenze; https://...; info@...\nB&B La Terrazza; B&B / Domus; Verona"}
              data-testid="import-textarea"
              className={`${inputCls} mt-4 resize-none font-mono text-xs`}
            />
            <button
              onClick={doImport}
              disabled={importing}
              data-testid="import-button"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 font-accent text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a] disabled:opacity-60"
            >
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {importing ? "Importo…" : "Importa nell'elenco"}
            </button>
          </motion.div>
        )}

        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="relative">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-faded" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca nome, città…"
              data-testid="prospects-search"
              className={`${inputCls} pl-10`}
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="prospects-filter-type"
            className={selectCls}
          >
            <option value="tutte">Tutte le categorie</option>
            {TYPES.map((t) => (
              <option key={t} value={t} className="bg-ink">
                {t}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="prospects-filter-status"
            className={selectCls}
          >
            <option value="tutti">Tutti gli stati</option>
            {STATUSES.map((s) => (
              <option key={s} value={s} className="bg-ink">
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-8 space-y-4">
          {loading && (
            <p className="py-16 text-center font-accent text-xs uppercase tracking-[0.28em] text-faded">
              Caricamento…
            </p>
          )}
          {!loading && filtered.length === 0 && (
            <div className="rounded-3xl border border-dashed border-line py-16 text-center">
              <ClipboardList size={28} className="mx-auto text-faded" />
              <p className="mt-4 text-sm text-stone">
                Nessuna attività in questo filtro. Importa il tuo elenco con «Importa attività».
              </p>
            </div>
          )}
          {filtered.map((p, i) => {
            const rep = reports[p.id];
            const isOpen = openId === p.id;
            return (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: Math.min(i * 0.04, 0.3), ease: EASE }}
                data-testid={`prospect-card-${i + 1}`}
                className={`rounded-3xl border bg-panel transition-colors duration-300 ${
                  isOpen ? "border-terracotta/50" : "border-line hover:border-line-strong"
                }`}
              >
                <div className="flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h2 className="font-serif text-xl tracking-tight text-bone">{p.name}</h2>
                      <span className="rounded-full border border-line px-3 py-1 font-accent text-[9px] uppercase tracking-[0.18em] text-stone">
                        {p.business_type}
                      </span>
                      <span
                        className={`rounded-full px-3 py-1 font-accent text-[9px] uppercase tracking-[0.18em] ${
                          p.status === "cliente"
                            ? "bg-cypress/30 text-[#b8d4bd]"
                            : p.status === "scartato"
                              ? "bg-panel-2 text-faded"
                              : "bg-terracotta/15 text-terracotta"
                        }`}
                      >
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-stone">
                      {p.city}
                      {p.region ? ` · ${p.region}` : ""}
                      {p.website && (
                        <>
                          {" · "}
                          <a
                            href={p.website.startsWith("http") ? p.website : `https://${p.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`prospect-website-${i + 1}`}
                            className="inline-flex items-center gap-1 text-ochre hover:underline"
                          >
                            sito attuale <ExternalLink size={11} />
                          </a>
                        </>
                      )}
                      {p.email && ` · ${p.email}`}
                    </p>
                    {p.notes && <p className="mt-1 text-xs italic text-faded">{p.notes}</p>}
                    <ProposalCode
                      prospect={p}
                      index={i + 1}
                      onUpdate={(upd) => setProspects((ps) => ps.map((x) => (x.id === upd.id ? upd : x)))}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-2.5">
                    <select
                      value={p.status}
                      onChange={(e) => setStatus(p, e.target.value)}
                      data-testid={`prospect-status-${i + 1}`}
                      className={`${selectCls} w-40 py-2.5 text-xs`}
                      aria-label={`Stato di ${p.name}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="bg-ink">
                          {STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => analyze(p)}
                      disabled={analyzingId !== null}
                      data-testid={`prospect-analyze-${i + 1}`}
                      className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 font-accent text-[10px] font-semibold uppercase tracking-[0.16em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a] disabled:opacity-60"
                    >
                      {analyzingId === p.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Sparkles size={13} />
                      )}
                      {analyzingId === p.id ? "Analizzo…" : "Analizza con IA"}
                    </button>
                    {rep && (
                      <button
                        onClick={() => (rep ? setOpenId(isOpen ? null : p.id) : openReport(p))}
                        data-testid={`prospect-report-${i + 1}`}
                        className="rounded-full border border-line-strong px-5 py-2.5 font-accent text-[10px] font-semibold uppercase tracking-[0.16em] text-bone transition-colors duration-300 hover:border-bone/50"
                      >
                        {isOpen ? "Chiudi" : "Vedi report"}
                      </button>
                    )}
                    {!rep && p.has_report && (
                      <button
                        onClick={() => openReport(p)}
                        data-testid={`prospect-report-${i + 1}`}
                        className="rounded-full border border-line-strong px-5 py-2.5 font-accent text-[10px] font-semibold uppercase tracking-[0.16em] text-bone transition-colors duration-300 hover:border-bone/50"
                      >
                        Vedi report
                      </button>
                    )}
                    <a
                      href={`/proposta/${p.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`proposta-create-${i + 1}`}
                      className="inline-flex items-center gap-2 rounded-full border border-ochre/50 px-5 py-2.5 font-accent text-[10px] font-semibold uppercase tracking-[0.16em] text-ochre transition-colors duration-300 hover:bg-ochre/10"
                    >
                      <FileSignature size={13} /> Crea proposta
                    </a>
                    <button
                      onClick={() => remove(p)}
                      data-testid={`prospect-delete-${i + 1}`}
                      aria-label={`Elimina ${p.name}`}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-faded transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    data-testid={`report-panel-${i + 1}`}
                    className="border-t border-line p-5 sm:p-6"
                  >
                    {analyzingId === p.id && !rep ? (
                      <p className="flex items-center gap-3 py-8 font-accent text-xs uppercase tracking-[0.24em] text-ochre">
                        <Loader2 size={16} className="animate-spin" />
                        Sto analizzando il sito attuale e preparo la proposta… 30–60 secondi
                      </p>
                    ) : rep ? (
                      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                        <div className="space-y-8">
                          <div>
                            <p className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.24em] text-terracotta">
                              <TriangleAlert size={13} /> Punti deboli del sito attuale
                            </p>
                            <ul className="mt-4 space-y-2.5">
                              {rep.punti_deboli.map((d, k) => (
                                <li key={k} className="flex gap-3 text-sm leading-relaxed text-stone">
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />
                                  {d}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.24em] text-ochre">
                              <TrendingUp size={13} /> Come raggiungere più clienti
                            </p>
                            <ul className="mt-4 space-y-2.5">
                              {rep.opportunita.map((o, k) => (
                                <li key={k} className="flex gap-3 text-sm leading-relaxed text-stone">
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-ochre" />
                                  {o}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        <div className="space-y-8">
                          <div>
                            <p className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.24em] text-terracotta">
                              <LayoutTemplate size={13} /> Bozza del nuovo sito
                            </p>
                            <p className="mt-3 font-serif text-xl italic tracking-tight text-bone">
                              «{rep.bozza_titolo}»
                            </p>
                            <ul className="mt-4 space-y-2.5">
                              {rep.bozza_pagine.map((pg, k) => (
                                <li key={k} className="rounded-xl border border-line bg-ink p-4">
                                  <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-terracotta">
                                    {pg.nome}
                                  </p>
                                  <p className="mt-1.5 text-sm leading-relaxed text-stone">
                                    {pg.contenuto}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="rounded-2xl border border-line bg-ink p-5">
                            <p className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.24em] text-terracotta">
                              <Mail size={13} /> Proposta email
                            </p>
                            <p className="mt-3 text-sm font-semibold text-bone">
                              Oggetto: {rep.email_oggetto}
                            </p>
                            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-stone">
                              {rep.email_corpo}
                            </p>
                            <div className="mt-5 flex flex-wrap gap-2.5">
                              <a
                                href={`mailto:${p.email || ""}?subject=${encodeURIComponent(rep.email_oggetto)}&body=${encodeURIComponent(rep.email_corpo)}`}
                                data-testid={`prospect-mailto-${i + 1}`}
                                onClick={p.email ? undefined : (e) => e.preventDefault()}
                                className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 font-accent text-[10px] font-semibold uppercase tracking-[0.16em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
                              >
                                <Mail size={13} /> Apri nella tua email
                              </a>
                              <button
                                onClick={() => copyEmail(rep)}
                                data-testid={`prospect-copy-email-${i + 1}`}
                                className="inline-flex items-center gap-2 rounded-full border border-line-strong px-5 py-2.5 font-accent text-[10px] font-semibold uppercase tracking-[0.16em] text-bone transition-colors duration-300 hover:border-bone/50"
                              >
                                <Copy size={13} /> Copia testo
                              </button>
                            </div>
                            {!p.email && (
                              <p className="mt-3 text-xs text-faded">
                                Nessuna email salvata per questa attività: copia il testo e
                                inviala dalla tua casella.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="py-6 text-sm text-faded">
                        Genera l'analisi per vedere punti deboli, bozza sito e proposta email.
                      </p>
                    )}
                  </motion.div>
                )}
              </motion.article>
            );
          })}
        </div>

        <p className="mt-12 text-center text-xs text-faded">
          Elenco di esempio pre-caricato per la dimostrazione: importando il tuo elenco reale lo
          sostituisci in un click.
        </p>
      </div>
      )}
    </main>
  );
}